/**
 * LicenseGate (P270.4) -- self-contained license overlay/banner.
 *
 * Mounted ONCE in the AppShell. Renders nothing in the common case
 * (enforcement off -- the v2 default -- or an active license), so it's
 * invisible until an operator flips `MARSAD_LICENSE_GATE_ENABLED=1` and a
 * license is installed. When enforcing:
 *   - missing / invalid / expired / revoked -> a blocking full-screen
 *     overlay (admins get the upload form; others get "contact admin").
 *   - grace -> a non-blocking top banner (read-only notice + days left).
 *
 * Hardcoded English to match the sibling shared/ exec surfaces.
 */
import { useTranslation } from "react-i18next";
import { useMe } from "shared/api/useMe";
import { LicenseUploadForm } from "shared/licensing/LicensePanel";
import { useLicenseStatus } from "shared/licensing/useLicense";

const BLOCKING = new Set(["missing", "invalid", "expired", "revoked"]);

export function LicenseGate() {
  const { t } = useTranslation();
  const status = useLicenseStatus();
  const me = useMe();
  const s = status.data;

  // Invisible unless enforcement is on AND the license isn't healthy.
  if (!s || !s.gate_enabled || s.state === "active") return null;

  const isAdmin = me.data?.is_admin ?? false;

  if (s.state === "grace") {
    return (
      <div
        data-testid="license-grace-banner"
        className="bg-amber-50 border-b border-amber-300 px-6 py-2 text-sm text-amber-900"
      >
        {t("screens.licenseGate.licenseExpiredReadOnlyGraceMode")}
        {s.grace_days_remaining != null && ` (${s.grace_days_remaining} day(s) left)`}.
        {isAdmin
          ? t("screens.licenseGate.uploadARenewalInAdminLicense")
          : t("screens.licenseGate.contactAnAdminToRenew")}
      </div>
    );
  }

  if (BLOCKING.has(s.state)) {
    return (
      <div
        data-testid="license-blocking-overlay"
        className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-navy/95 p-6"
      >
        <div className="max-w-md rounded-lg bg-white p-6 shadow-2xl space-y-3">
          <h2 className="font-display text-xl text-slate-900">
            {t("screens.licenseGate.licenseRequired")}
          </h2>
          <p className="text-sm text-slate-600">
            {s.error_message ||
              `This deployment's license is ${s.state}. MARSAD is locked until a valid license is installed.`}
          </p>
          {isAdmin ? (
            <div className="pt-1">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("screens.licenseGate.installALicense")}
              </p>
              <LicenseUploadForm compact />
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              {t("screens.licenseGate.contactAMarsadAdministratorToInstallOr")}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
}
